import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../cn";
import { Button } from "./button";

export type PropsRkModal = {
  readonly aberto: boolean;
  readonly titulo: string;
  readonly descricao?: string;
  readonly onFechar: () => void;
  readonly children?: ReactNode;
  readonly className?: string;
  /** Quando false, omite o botao Fechar padrao (use acoes no children). */
  readonly mostrarFechar?: boolean;
  /** X no canto do titulo. */
  readonly mostrarX?: boolean;
  /** Esc fecha o dialogo. Formularios longos deixam isso desligado. */
  readonly fecharComEsc?: boolean;
  readonly rodape?: ReactNode;
  /**
   * Conteudo ao lado direito do dialogo (ex.: preview de midia).
   * No desktop fica ao lado; no mobile empilha abaixo.
   */
  readonly lateral?: ReactNode;
  /** Largura máxima do painel em px (padrão 560). */
  readonly largura?: number;
  /**
   * Trava o topo em pixels ao abrir (padrão). Formulário com seletor de
   * arquivo precisa disso; conteúdo que carrega depois (preview) não: travado,
   * ele cresce para baixo e sai do centro.
   */
  readonly travarTopo?: boolean;
};

/**
 * Overlay de dialogo. Renderiza em `document.body` para nao ficar preso
 * no stacking context da moldura (sidebar z-20 vs conteudo z-10).
 */
export function Modal({
  aberto,
  titulo,
  descricao,
  onFechar,
  children,
  className,
  mostrarFechar = true,
  mostrarX = false,
  fecharComEsc = true,
  rodape,
  lateral,
  largura = 560,
  travarTopo = true,
}: PropsRkModal) {
  const tituloId = useId();
  const descId = useId();
  const painelRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const onFecharRef = useRef(onFechar);
  onFecharRef.current = onFechar;
  const [topoFixo, setTopoFixo] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!aberto) {
      setTopoFixo(null);
      return;
    }
    const el = layoutRef.current;
    if (!el || topoFixo !== null || !travarTopo) return;
    // Trava o topo em pixels. O seletor de arquivo do sistema muda o viewport
    // e o place-items: center empurrava o dialogo para cima.
    setTopoFixo(Math.max(20, Math.round(el.getBoundingClientRect().top)));
  }, [aberto, topoFixo, travarTopo]);

  useEffect(() => {
    if (!aberto) return;
    const anterior = document.activeElement as HTMLElement | null;
    // Foco so na abertura: se rodar a cada render, rouba o cursor do input.
    painelRef.current?.focus();
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function aoTecla(e: KeyboardEvent) {
      if (!fecharComEsc) return;
      if (e.key === "Escape") onFecharRef.current();
    }
    document.addEventListener("keydown", aoTecla);
    return () => {
      document.removeEventListener("keydown", aoTecla);
      document.body.style.overflow = overflowAnterior;
      anterior?.focus?.();
    };
  }, [aberto, fecharComEsc]);

  if (!aberto || typeof document === "undefined") return null;

  return createPortal(
    <div className="rk-modal-root">
      <button
        type="button"
        className="rk-modal-backdrop"
        aria-label="Fechar"
        onClick={onFechar}
      />
      <div
        className={cn("rk-modal", lateral ? "rk-modal--com-lateral" : undefined)}
        role="presentation"
      >
        <div
          ref={layoutRef}
          className="rk-modal__layout"
          style={
            {
              "--rk-modal-largura": `${largura}px`,
              ...(topoFixo === null
                ? {}
                : {
                    position: "absolute",
                    top: topoFixo,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: `min(calc(100% - 40px), ${largura}px)`,
                  }),
            } as CSSProperties
          }
        >
          <div
            ref={painelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={tituloId}
            aria-describedby={descricao ? descId : undefined}
            tabIndex={-1}
            className={cn("rk-modal__panel", "rk-focusable", className)}
          >
            <div className="rk-modal__topo">
            {mostrarX ? (
              <div className="rk-modal__cabecalho">
                <h2 id={tituloId} className="rk-heading-md" style={{ marginTop: 0 }}>
                  {titulo}
                </h2>
                <button
                  type="button"
                  className="rk-modal__fechar rk-focusable"
                  aria-label="Fechar"
                  onClick={onFechar}
                >
                  <X aria-hidden="true" size={18} />
                </button>
              </div>
            ) : (
              <h2 id={tituloId} className="rk-heading-md" style={{ marginTop: 0 }}>
                {titulo}
              </h2>
            )}
            {descricao ? (
              <p id={descId} className="rk-modal__descricao">
                {descricao}
              </p>
            ) : null}
            </div>
            {children}
            {rodape ? (
              <div className="rk-modal__rodape">{rodape}</div>
            ) : mostrarFechar ? (
              <div className="rk-modal__rodape rk-modal__rodape--fim">
                <Button variante="secondary" tamanho="sm" onClick={onFechar}>
                  Fechar
                </Button>
              </div>
            ) : null}
          </div>
          {lateral ? (
            <aside className="rk-modal__lateral" aria-label="Prévia">
              {lateral}
            </aside>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
